import { useCallback, useEffect, useRef, useState } from 'react';
import { dailyJournalNoteId, humanizeApiError, type AgendaSettings } from '@lapo/core';
import type { AgendaSectionState, GoogleAccountView, ToastInput } from '@lapo/ui';
import type { AgendaDayDTO, ApiClient } from './auth/apiClient.js';
import type { AuthSession } from './auth/provider.js';
import { API_URL } from './config.js';
import { panelCacheKey, readPanelCache, writePanelCache } from './panelCache.js';

/**
 * Owns the Daily Agenda: both the drawer's day view of every calendar event (the Agenda right-panel)
 * AND the server-side agenda settings edited on the Integrations hub's Daily Agenda detail page. The
 * agenda panel keeps its OWN viewed day (`agendaDateKey`), independent of the main journal date —
 * day navigation here never touches routing. Behavior-preserving extraction from App.tsx: the panel
 * hydrates "today" from the last-known cache and refreshes when it becomes visible or the viewed day
 * changes; stale in-flight responses for a paged-away day are dropped; the settings load whenever the
 * config surface is visible; Save persists via the API; "Run now" composes today's agenda on demand.
 *
 * Deps are passed in (never reached out of App): `api`, the auth `session` (uid + gating), `pushToast`,
 * `agendaPanelVisible` (App computes `rightOpen && RIGHT_VIEWS[rightViewIndex]?.id === 'agenda'` and
 * passes it, reproducing the inline effect's exact gate), `agendaConfigVisible` (App computes
 * `settingsOpen || (view.kind === 'integration' && view.id === 'daily-agenda')`), and `calendarAccounts`
 * (App's `accounts.filter((a) => a.integrations.includes('calendar'))`, fed into the settings section
 * object). `session` null (signed out) keeps everything empty/local. `AgendaPanel`'s `onOpenEventNote`
 * stays in App (it routes) and is wired straight into the panel JSX — not owned here.
 */

/** Client-local YYYY-MM-DD for a Date, so "today" honors the viewer's timezone (not the server's). */
function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** `dateKey` shifted by `delta` days, staying in YYYY-MM-DD. */
function shiftDateKey(dateKey: string, delta: number): string {
  const [y, m, d] = dateKey.slice(0, 10).split('-').map(Number);
  const base = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  base.setDate(base.getDate() + delta);
  return localDateKey(base);
}

export interface UseAgendaDeps {
  api: ApiClient;
  session: AuthSession | null;
  pushToast: (t: ToastInput) => number;
  /** True when the Agenda panel is the visible view in the open right drawer. */
  agendaPanelVisible: boolean;
  /** True when a surface that shows the agenda settings is visible (Settings modal OR the hub detail page). */
  agendaConfigVisible: boolean;
  /** Linked Google accounts that grant calendar — fed into the settings section object. */
  calendarAccounts: GoogleAccountView[];
}

export interface UseAgenda {
  // Agenda panel (drawer day view).
  agendaDateKey: string;
  agendaDay: AgendaDayDTO | null;
  agendaDayLoading: boolean;
  agendaDayError: string | null;
  onAgendaRefresh: () => void;
  onAgendaPrevDay: () => void;
  onAgendaNextDay: () => void;
  onAgendaToday: () => void;
  // Agenda settings (Integrations hub detail page).
  agenda: AgendaSectionState | undefined;
  onSaveAgenda: (next: AgendaSettings) => Promise<void>;
  onRunAgenda: () => Promise<void>;
}

export function useAgenda({
  api,
  session,
  pushToast,
  agendaPanelVisible,
  agendaConfigVisible,
  calendarAccounts,
}: UseAgendaDeps): UseAgenda {
  // Track the live session in a ref so the async loader can gate on the current identity without
  // re-creating on every session change (mirrors App's sessionRef guard).
  const sessionRef = useRef<AuthSession | null>(session);
  sessionRef.current = session;

  // Agenda — the drawer's day view of every calendar event. The viewed day lives here; AgendaPanel is
  // presentational and owns its own per-minute "now" tick (audit F4 — it used to live in App state and
  // re-render the whole app every minute).
  const [agendaDateKey, setAgendaDateKey] = useState<string>(() => localDateKey(new Date()));
  const [agendaDay, setAgendaDay] = useState<AgendaDayDTO | null>(null);
  const [agendaDayLoading, setAgendaDayLoading] = useState(false);
  const [agendaDayError, setAgendaDayError] = useState<string | null>(null);
  const agendaDateKeyRef = useRef(agendaDateKey);
  agendaDateKeyRef.current = agendaDateKey;

  const loadAgendaDay = useCallback(
    async (dateKey: string) => {
      if (!API_URL || !sessionRef.current) return;
      setAgendaDayLoading(true);
      setAgendaDayError(null);
      try {
        const day = await api.getAgendaDay(dateKey);
        // Drop a stale response if the user paged to another day while it was in flight.
        if (agendaDateKeyRef.current === dateKey) setAgendaDay(day);
        // Cache only "today" (single key, like Todoist) so the panel can hydrate instantly next open.
        const uid = sessionRef.current?.user.uid;
        if (uid && dateKey === localDateKey(new Date())) writePanelCache(panelCacheKey('agenda-today', uid), day);
      } catch (err) {
        if (agendaDateKeyRef.current === dateKey) setAgendaDayError(humanizeApiError(err));
      } finally {
        setAgendaDayLoading(false);
      }
    },
    [api],
  );

  // Load whenever the Agenda panel is visible or the viewed day changes. Today hydrates from cache so
  // it paints instantly (spinner-refresh behind it); other days blank first so a paged-to day never
  // briefly shows the previous day's list.
  useEffect(() => {
    if (!agendaPanelVisible) return;
    const uid = session?.user.uid;
    const cached =
      uid && agendaDateKey === localDateKey(new Date())
        ? readPanelCache<AgendaDayDTO>(panelCacheKey('agenda-today', uid))
        : null;
    setAgendaDay(cached && cached.dateKey === agendaDateKey ? cached : null);
    void loadAgendaDay(agendaDateKey);
  }, [agendaPanelVisible, agendaDateKey, session, loadAgendaDay]);

  // Stable Agenda-panel handlers (audit F3): AgendaPanel is memoized, so its props must not be
  // re-minted inline each App render or the memo never bails out.
  const onAgendaRefresh = useCallback(() => {
    void loadAgendaDay(agendaDateKeyRef.current);
  }, [loadAgendaDay]);
  const onAgendaPrevDay = useCallback(() => setAgendaDateKey((k) => shiftDateKey(k, -1)), []);
  const onAgendaNextDay = useCallback(() => setAgendaDateKey((k) => shiftDateKey(k, 1)), []);
  const onAgendaToday = useCallback(() => setAgendaDateKey(localDateKey(new Date())), []);

  // Daily Agenda settings (server-side, per user). Loaded with the dialog; Save persists via the API
  // (the server re-validates). "Run now" composes today's agenda on demand for a quick preview.
  const [agendaSettings, setAgendaSettings] = useState<AgendaSettings | null>(null);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [agendaSaving, setAgendaSaving] = useState(false);
  const [agendaRunning, setAgendaRunning] = useState(false);
  const [agendaError, setAgendaError] = useState<string | undefined>(undefined);
  const [agendaRunNote, setAgendaRunNote] = useState<string | undefined>(undefined);

  // The agenda config UI lives on the Integrations hub's Daily Agenda detail page (moved out of the old
  // Settings → Daily Agenda section), so load whenever either surface is visible — otherwise the hub
  // page renders its "Unavailable" fallback because `agendaSettings` is still null.
  useEffect(() => {
    if (!agendaConfigVisible || !session || !API_URL) return;
    setAgendaLoading(true);
    setAgendaError(undefined);
    void (async () => {
      try {
        setAgendaSettings(await api.getAgendaConfig());
      } catch (err) {
        setAgendaError(`Couldn't load agenda settings: ${humanizeApiError(err)}`);
      } finally {
        setAgendaLoading(false);
      }
    })();
  }, [agendaConfigVisible, session, api]);

  const onSaveAgenda = useCallback(
    async (next: AgendaSettings) => {
      setAgendaSaving(true);
      setAgendaError(undefined);
      try {
        setAgendaSettings(await api.putAgendaConfig(next));
        pushToast({ tone: 'success', message: 'Agenda settings saved.' });
      } catch (err) {
        setAgendaError(humanizeApiError(err));
        pushToast({ tone: 'error', title: 'Could not save', message: humanizeApiError(err) });
      } finally {
        setAgendaSaving(false);
      }
    },
    [api, pushToast],
  );

  const onRunAgenda = useCallback(async () => {
    setAgendaRunning(true);
    setAgendaRunNote(undefined);
    try {
      const dateKey = dailyJournalNoteId(new Date()).replace('journal-', '');
      const tz = agendaSettings?.timezone;
      const { markdown, eventCount } = await api.runAgenda(dateKey, tz);
      const note = markdown ? `Composed ${eventCount} event${eventCount === 1 ? '' : 's'} for ${dateKey}.` : 'Nothing scheduled today.';
      setAgendaRunNote(note);
      pushToast({ tone: 'success', message: note });
    } catch (err) {
      setAgendaError(humanizeApiError(err));
      pushToast({ tone: 'error', title: 'Could not run agenda', message: humanizeApiError(err) });
    } finally {
      setAgendaRunning(false);
    }
  }, [api, agendaSettings, pushToast]);

  const agenda: AgendaSectionState | undefined =
    session && API_URL && agendaSettings
      ? {
          settings: agendaSettings,
          accounts: calendarAccounts,
          loading: agendaLoading,
          saving: agendaSaving,
          running: agendaRunning,
          error: agendaError,
          lastRunNote: agendaRunNote,
        }
      : undefined;

  return {
    agendaDateKey,
    agendaDay,
    agendaDayLoading,
    agendaDayError,
    onAgendaRefresh,
    onAgendaPrevDay,
    onAgendaNextDay,
    onAgendaToday,
    agenda,
    onSaveAgenda,
    onRunAgenda,
  };
}
